<script setup lang="ts">
// The closed state inside a feature-chat panel (v3 `OrpheusConsentCompact.vue`): too narrow for
// the full consent screen, so an owner/admin turns Orpheus on through the consent text in a modal,
// and a member can see who to ask.
import { computed, ref } from 'vue'

import { presentAdmins } from '../../service/orpheusConsent'
import { useOrpheusConsentStore } from '../../stores/orpheusConsentStore'

import OrpheusConsentCopy from './OrpheusConsentCopy.vue'

import { useAccount } from '@/features/account'
import { useSettingsRbac } from '@/features/workspace-settings'
import { ApiError } from '@/shared/http'
import { useI18n } from '@/shared/i18n'
import { Button, Icon, Modal, useToast } from '@/shared/ui'

const { t } = useI18n()
const toast = useToast()
const { accountId } = useAccount()
const { canManageAccount } = useSettingsRbac()
const consent = useOrpheusConsentStore()
const K = 'orpheus.consent.compact'

const showAdmins = ref(false)
const modalOpen = ref(false)
const granting = ref(false)
const admins = computed(() => presentAdmins(consent.accountAdmins))

async function grant(): Promise<void> {
  const account = accountId.value
  if (account === undefined || granting.value) return
  granting.value = true
  try {
    await consent.grant(account)
    modalOpen.value = false
  } catch (cause) {
    const error = ApiError.isApiError(cause) ? cause : ApiError.fromAxios(cause)
    toast.error(
      error.userVisible && error.status !== null && error.detail
        ? error.detail
        : t('orpheus.consentSettings.error.grantFailed'),
    )
  } finally {
    granting.value = false
  }
}
</script>

<template>
  <div
    class="orpheus-consent-compact flex h-full flex-col items-center justify-center gap-4 px-6 py-10 text-center"
    data-testid="orpheus-consent-compact"
  >
    <span
      class="flex h-12 w-12 items-center justify-center rounded-full bg-surface-sunken text-muted"
      aria-hidden="true"
    >
      <Icon name="lock" :size="20" weight="light" />
    </span>

    <div class="flex flex-col gap-1">
      <p class="m-0 text-sm font-semibold text-fg">{{ t('orpheus.consent.closed.title') }}</p>
      <p class="m-0 text-xs text-muted">
        {{ t(canManageAccount ? `${K}.manageBody` : `${K}.memberBody`) }}
      </p>
    </div>

    <div
      class="w-full max-w-xs rounded-lg border border-orpheus-muted bg-surface-sunken px-3 py-2.5 text-left text-sm text-muted"
      aria-disabled="true"
    >
      {{ t('orpheus.consent.closed.title') }}
    </div>

    <Button
      v-if="canManageAccount"
      size="sm"
      data-testid="orpheus-consent-compact-turn-on"
      @click="modalOpen = true"
    >
      {{ t('orpheus.consent.screen.turnOn') }}
    </Button>

    <template v-else>
      <button
        type="button"
        class="cursor-pointer border-0 bg-transparent p-0 text-xs font-semibold text-primary underline"
        data-testid="orpheus-consent-compact-who"
        @click="showAdmins = !showAdmins"
      >
        {{ t(`${K}.who`) }}
      </button>

      <ul v-if="showAdmins && admins.length" class="m-0 flex w-full list-none flex-col gap-2 p-0">
        <li
          v-for="admin in admins"
          :key="admin.email"
          class="flex items-center justify-between gap-2 rounded-md border border-orpheus-muted bg-surface px-3 py-2 text-left"
        >
          <span class="flex min-w-0 flex-col">
            <span class="truncate text-xs font-medium text-fg">{{ admin.name }}</span>
            <span class="truncate text-xs text-muted">{{ admin.email }}</span>
          </span>
          <span class="shrink-0 text-xs text-muted">
            {{ t(`orpheus.consent.role.${admin.role}`) }}
          </span>
        </li>
      </ul>
    </template>

    <Modal v-model="modalOpen" size="lg" :title="t('orpheus.consentSettings.grantTitle')">
      <OrpheusConsentCopy />
      <template #footer>
        <Button variant="data" :disabled="granting" @click="modalOpen = false">
          {{ t('orpheus.consentSettings.cancel') }}
        </Button>
        <Button :loading="granting" data-testid="orpheus-consent-compact-accept" @click="grant">
          {{ t('orpheus.consent.screen.turnOn') }}
        </Button>
      </template>
    </Modal>
  </div>
</template>
